import type { Locale } from './types';

export interface LocaleMatch {
  precise: Locale | null;
  approximate: Locale | null;
}

/**
 * Parse a raw locale code into normalized {lang, country}.
 * - `en-GB` → `{lang: 'en', country: 'GB'}`
 * - `en` → `{lang: 'en', country: null}`
 * - `EN-gb` → `{lang: 'en', country: 'GB'}` (case-normalized)
 * - `null` / `''` → `{lang: null, country: null}`
 */
export function normalizeLocaleCode(rawLocaleCode: string | null | undefined): {
  lang: string | null;
  country: string | null;
} {
  const [rawLang, rawCountry] = (rawLocaleCode || '').split('-');
  return {
    lang: (rawLang || '').toLowerCase() || null,
    country: (rawCountry || '').toUpperCase() || null,
  };
}

/**
 * Try to locate `rawLocaleCode` in the given `localeList`. Returns both:
 * - `precise`: exact `lang-COUNTRY` match
 * - `approximate`: first locale sharing the same `lang` prefix (e.g. `en-US`
 *   is an approximate for a requested `en-GB` when only `en-US` is enabled)
 *
 * Matches by exact language prefix (`lang === requested`), NOT startsWith —
 * so a hypothetical `zha-XX` locale won't incorrectly match `zh`.
 */
export function findLocaleMatch(
  rawLocaleCode: string | null | undefined,
  localeList: readonly Locale[]
): LocaleMatch {
  const { lang, country } = normalizeLocaleCode(rawLocaleCode);
  return {
    precise:
      localeList.find((locale) => locale.code === `${lang}-${country}`) ?? null,
    approximate:
      localeList.find((locale) => locale.code.split('-')[0] === lang) ?? null,
  };
}

export function shortenLocaleCode(
  fullLocaleCode: string | null | undefined
): string {
  return (fullLocaleCode || '').split('-')[0] || '';
}
